import { useEffect, useMemo, useState } from 'react';
import useContactStore from '@/stores/ContactStore';
import useInboxStore from '@/stores/InboxStore';
import useUserStore from '@/stores/UserStore';
import { Check, Loader2, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { trackLogOutAndMixpanelReset } from '@/lib/analytics-event';
import { removeToken } from '@/lib/utils';
import {
  fetchAvailableInstagramConnections,
  selectInstagramConnection,
} from '@/hooks/user';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';

const InstagramSelectPage = () => {
  const navigate = useNavigate();
  const nextOnboardingStage = useUserStore(
    (state) => state.nextOnboardingStage
  );
  const user = useUserStore((state) => state.user);
  const resetUserStore = useUserStore((state) => state.resetUserStore);
  const resetContactStore = useContactStore((state) => state.resetContactStore);
  const resetInboxStore = useInboxStore((state) => state.resetInboxStore);

  const [loading, setLoading] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [connections, setConnections] = useState([]);
  const [selectedUuid, setSelectedUuid] = useState(null);

  // Keep reference if needed for future; avoid unused variable warning
  useMemo(
    () => connections.find((c) => c.uuid === selectedUuid) || null,
    [connections, selectedUuid]
  );

  useEffect(() => {
    // Wait for user data to be loaded before checking onboarding stage
    if (user) {
      setIsDataLoaded(true);
    }
  }, [user]);

  useEffect(() => {
    // Navigate user to appropriate page based on their onboarding stage
    if (isDataLoaded) {
      if (nextOnboardingStage === 'additional_details') {
        navigate('/onboarding/setup');
      } else if (nextOnboardingStage === 'connect_account') {
        navigate('/onboarding/connect-channel');
      } else if (nextOnboardingStage !== 'pending_selection') {
        navigate('/inbox');
      }
    }
  }, [isDataLoaded, nextOnboardingStage, navigate]);

  useEffect(() => {
    const fetchInstagramAccounts = async () => {
      setLoading(true);
      try {
        const response = await fetchAvailableInstagramConnections();
        if (response?.status === 'success') {
          const available = response?.data?.available_connections || [];
          setConnections(available);
        } else {
          toast.error('Failed to fetch Instagram connections');
        }
      } catch (error) {
        console.error(error);
        toast.error('Failed to fetch Instagram connections');
      } finally {
        setLoading(false);
      }
    };
    fetchInstagramAccounts();
  }, []);

  const handleSelect = async () => {
    if (!selectedUuid) return;
    setSelecting(true);
    try {
      const response = await selectInstagramConnection(selectedUuid);
      if (response?.status === 'success') {
        toast.success('Instagram connection selected');
        navigate('/inbox', { replace: true });
      } else {
        toast.error('Failed to select Instagram connection');
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to select Instagram connection');
    } finally {
      setSelecting(false);
    }
  };

  const handleLogout = () => {
    removeToken();

    // reset user store
    resetUserStore();
    // reset contact store
    resetContactStore();
    // reset inbox store
    resetInboxStore();

    // reset mixpanel
    trackLogOutAndMixpanelReset();
    // navigate to login page
    navigate('/login');

    // refresh the page to ensure all state is cleared
    window.location.reload();
  };

  // Show loading state while user data is being fetched
  if (!isDataLoaded) {
    return (
      <div className='flex h-screen items-center justify-center bg-background'>
        <div className='flex items-center gap-2'>
          <Loader2 className='h-6 w-6 animate-spin' />
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className='relative flex min-h-screen items-center justify-center bg-background p-4'>
      {/* Logout Button - Top Right */}
      <div className='absolute right-4 top-4'>
        <Button
          variant='ghost'
          size='sm'
          onClick={handleLogout}
          className='flex items-center gap-2'
        >
          <LogOut className='h-4 w-4' />
          Logout
        </Button>
      </div>

      <div className='w-full max-w-4xl space-y-8'>
        {/* Header */}
        <div className='space-y-4 text-center'>
          <h1 className='text-3xl font-bold'>Select Your Instagram Page</h1>
          <p className='text-lg text-muted-foreground'>
            Choose which Instagram business account you&apos;d like to connect
            to get started with Revio
          </p>
        </div>

        {/* Instagram Pages Grid */}
        {loading ? (
          <div className='flex items-center justify-center py-12'>
            <div className='text-lg text-muted-foreground'>
              Loading available Instagram pages...
            </div>
          </div>
        ) : (
          <div className='grid gap-6 md:grid-cols-2 lg:grid-cols-3'>
            {connections.map((conn) => {
              const ig = conn.ig_details || {};
              const isSelected = selectedUuid === conn.uuid;

              return (
                <div
                  key={conn.uuid}
                  className={`relative cursor-pointer rounded-lg border-2 p-6 transition-all hover:shadow-md ${
                    isSelected
                      ? 'border-primary bg-primary/5 shadow-md'
                      : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => setSelectedUuid(conn.uuid)}
                >
                  {/* Selection indicator */}
                  {isSelected && (
                    <div className='absolute right-4 top-4'>
                      <div className='flex h-6 w-6 items-center justify-center rounded-full bg-primary'>
                        <Check className='h-4 w-4 text-white' />
                      </div>
                    </div>
                  )}

                  <div className='flex items-center space-x-4'>
                    <Avatar className='h-16 w-16 border-2 border-border'>
                      <AvatarImage
                        src={ig.profile_pic_url || undefined}
                        alt={ig.ig_username || 'instagram'}
                      />
                      <AvatarFallback>
                        {(ig.ig_username || 'IG').substring(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className='min-w-0 flex-1'>
                      <h3 className='truncate text-lg font-semibold text-foreground'>
                        {ig.ig_name || 'Unnamed Page'}
                      </h3>
                      <p className='truncate text-sm text-muted-foreground'>
                        @{ig.ig_username || 'username'}
                      </p>
                      {ig.followers_count !== undefined && (
                        <p className='mt-1 text-xs text-muted-foreground'>
                          {ig.followers_count.toLocaleString()} followers
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Continue Button */}
        <div className='flex justify-center'>
          <Button disabled={!selectedUuid || selecting} onClick={handleSelect}>
            {selecting ? 'Connecting...' : 'Continue with Selected Page'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InstagramSelectPage;
